/**
 * BackgroundAudio - 배경 음악/소리 컨트롤러
 *
 * Heyzine 스타일의 배경 오디오 기능
 * 독서 분위기 조성을 위한 앰비언트 사운드
 *
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../i18n';
import {
  FiMusic,
  FiVolume2,
  FiVolumeX,
  FiPlay,
  FiPause,
  FiX,
  FiUpload,
} from 'react-icons/fi';

// 기본 앰비언트 사운드 (무료 공개 소스 활용 가능)
const DEFAULT_SOUNDS = [
  { id: 'rain', name: '빗소리', icon: '🌧️', url: '' },
  { id: 'forest', name: '숲속', icon: '🌲', url: '' },
  { id: 'cafe', name: '카페', icon: '☕', url: '' },
  { id: 'fireplace', name: '벽난로', icon: '🔥', url: '' },
  { id: 'ocean', name: '파도', icon: '🌊', url: '' },
  { id: 'night', name: '밤', icon: '🌙', url: '' },
];

interface BackgroundAudioProps {
  isOpen: boolean;
  onClose: () => void;
}

const BackgroundAudio = ({ isOpen, onClose }: BackgroundAudioProps) => {
  const { t } = useI18n();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [currentSound, setCurrentSound] = useState<string | null>(null);
  const [customAudioUrl, setCustomAudioUrl] = useState<string | null>(null);
  const [customAudioName, setCustomAudioName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 볼륨 변경
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // 사운드 재생/정지
  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  // 사운드 선택
  const handleSoundSelect = (soundId: string) => {
    const sound = DEFAULT_SOUNDS.find((s) => s.id === soundId);
    if (!sound) return;

    setCurrentSound(soundId);
    setCustomAudioUrl(null);
    setCustomAudioName('');

    // URL이 있으면 재생 (실제 URL이 없으면 기본 오디오 사용)
    if (sound.url && audioRef.current) {
      audioRef.current.src = sound.url;
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  // 커스텀 오디오 업로드
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setCustomAudioUrl(url);
    setCustomAudioName(file.name);
    setCurrentSound(null);

    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  // 정리
  useEffect(() => {
    return () => {
      if (customAudioUrl) {
        URL.revokeObjectURL(customAudioUrl);
      }
    };
  }, [customAudioUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiMusic className="text-primary" />
            {t('audio.title') || '배경 음악'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <FiX size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} loop />

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Now Playing */}
          {(currentSound || customAudioUrl) && (
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-xl">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                {currentSound ? (
                  <span className="text-2xl">
                    {DEFAULT_SOUNDS.find((s) => s.id === currentSound)?.icon || '🎵'}
                  </span>
                ) : (
                  <FiMusic size={24} className="text-primary" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('audio.nowPlaying') || '현재 재생 중'}
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentSound
                    ? DEFAULT_SOUNDS.find((s) => s.id === currentSound)?.name
                    : customAudioName}
                </p>
              </div>
              <button
                onClick={togglePlay}
                className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                {isPlaying ? <FiPause size={24} /> : <FiPlay size={24} />}
              </button>
            </div>
          )}

          {/* Volume Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('audio.volume') || '볼륨'}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {Math.round(volume * 100)}%
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setVolume(0)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
              >
                <FiVolumeX size={20} />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full appearance-none cursor-pointer accent-primary"
              />
              <button
                onClick={() => setVolume(1)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
              >
                <FiVolume2 size={20} />
              </button>
            </div>
          </div>

          {/* Preset Sounds */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('audio.presets') || '프리셋 사운드'}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {DEFAULT_SOUNDS.map((sound) => (
                <button
                  key={sound.id}
                  onClick={() => handleSoundSelect(sound.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    currentSound === sound.id
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="text-2xl mb-1">{sound.icon}</div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {sound.name}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Upload */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('audio.custom') || '나만의 음악'}
            </h3>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 transition-all"
            >
              <FiUpload size={20} className="text-gray-500 dark:text-gray-400" />
              <span className="text-gray-600 dark:text-gray-400">
                {t('audio.uploadHint') || 'MP3/WAV 파일 업로드'}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="p-3 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
              💡 {t('audio.tip') || '배경 음악으로 더 집중해서 책을 읽어보세요!'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundAudio;
